import React from "react";
import PropTypes from "prop-types";

/**
 * The EasyPlanner brand mark and wordmark.
 *
 * The mark is inline SVG rather than a glyph from Bootstrap Icons / Boxicons,
 * which is what the old QuickBook logo used. An icon font is the wrong carrier
 * for a logo: it renders nothing until the font loads, it cannot be exported to
 * a favicon or an og:image, and swapping the icon set would silently change the
 * brand. Inline SVG paints with the first frame and is the same geometry
 * everywhere.
 *
 * The tile is `currentColor` and the glyph white, so a parent sets the colour
 * once (`text-brand-600`) and the whole mark follows. Do not hard-code the
 * green here; it lives in the `@theme` block in index.css and changing it in one
 * place has to be enough. Over a brand-filled surface the mark needs a
 * contrasting tile, not `text-white` — that would paint tile and glyph alike.
 *
 * The glyph is Boxicons' `bx-calendar-check` (24-unit grid), copied out of the
 * font as a path at the size the "Today's schedule" card in BookingPreview
 * shows it: 18px on a 32px `rounded-lg` tile. A calendar page with a check —
 * a booking made.
 */
export const LogoMark = ({ className = "", title }) => (
  <svg
    viewBox="0 0 32 32"
    className={className}
    role={title ? "img" : "presentation"}
    aria-hidden={title ? undefined : "true"}
    focusable="false"
  >
    {title ? <title>{title}</title> : null}
    <rect width="32" height="32" rx="8" fill="currentColor" />
    <path
      transform="translate(7 7) scale(0.75)"
      fill="#fff"
      d="M19 4h-2V2h-2v2H9V2H7v2H5c-1.103 0-2 .897-2 2v14c0 1.103.897 2 2 2h14c1.103 0 2-.897 2-2V6c0-1.103-.897-2-2-2zm.002 16H5V8h14l.002 12zM11 17.414l5.707-5.707-1.414-1.414L11 14.586l-2.293-2.293-1.414 1.414z"
    />
  </svg>
);

LogoMark.propTypes = {
  className: PropTypes.string,
  /** Accessible name. Omit when a visible wordmark already names the brand. */
  title: PropTypes.string,
};

/**
 * Mark plus wordmark, laid out as a row.
 *
 * `markClassName` and `className` are passed through rather than derived from a
 * size prop: the header and the dashboard sidebar want different sizes at
 * different breakpoints, and Tailwind cannot build class names at runtime.
 */
const Logo = ({ className = "", markClassName = "size-8" }) => (
  <span className={`inline-flex items-center gap-2.5 ${className}`}>
    <LogoMark className={`shrink-0 text-brand-600 ${markClassName}`} />
    <span className="font-bold tracking-tight text-ink-900">
      Easy<span className="text-brand-600">Planner</span>
    </span>
  </span>
);

Logo.propTypes = {
  className: PropTypes.string,
  markClassName: PropTypes.string,
};

export default Logo;
